import { ParsedUrlQuery, ParsedUrlQueryInput } from 'querystring';
import { UrlObject } from 'url';

/** Present home city/category as the same path parameters used by /s. */
export const getHomeSearchQuery = (query: ParsedUrlQuery, currentCity: string): ParsedUrlQuery => {
  const { application, download_source, city, category, ...filters } = query;
  const selectedCity = typeof city === 'string' ? city : currentCity || 'ir';
  const selectedCategory = typeof category === 'string' ? category : undefined;
  return { ...filters, params: selectedCategory ? [selectedCity, selectedCategory] : selectedCity === 'ir' ? [] : [selectedCity] };
};

const withoutPage = (query: Record<string, unknown>, ignore: string[] = []) =>
  JSON.stringify(
    Object.entries(query)
      .filter(([key, value]) => ![...ignore, 'page'].includes(key) && value !== undefined && value !== '')
      .map(([key, value]) => [key, String(value)])
      .sort(),
  );

/**
 * "More results" (page) and picking a city stay on home. Any other search change — filters, sort, text —
 * opens the real search page, keeping the installed-app flags. Other navigation is untouched.
 */
export const getHomeSearchUrl = (
  url: string | UrlObject,
  shellQuery: ParsedUrlQuery,
  currentQuery: ParsedUrlQuery = {},
): string | UrlObject => {
  let destination: UrlObject;
  if (typeof url === 'string') {
    if (!/^\/s(?:\/|$|\?)/.test(url)) return url;
    const parsed = new URL(url, 'https://paziresh24.com');
    destination = { pathname: parsed.pathname, query: Object.fromEntries(parsed.searchParams) };
  } else destination = url;
  if (!/^\/s(?:\/|$)/.test(destination.pathname || '')) return url;
  const [city, category] = (destination.pathname || '').split('/').filter(Boolean).slice(1).map(decodeURIComponent);
  const destinationQuery = (typeof destination.query === 'object' ? destination.query : {}) as ParsedUrlQueryInput;
  const home = { ...shellQuery, ...destinationQuery, ...(city && { city }), ...(category && { category }) };
  // Pagination never leaves home: a page change keeps the current home search and only adds the page.
  if (destinationQuery.page !== undefined && String(destinationQuery.page) !== String(currentQuery.page ?? 1))
    return { pathname: '/apphome', query: { ...shellQuery, ...currentQuery, page: destinationQuery.page } };
  if (withoutPage(home) === withoutPage(currentQuery)) return { pathname: '/apphome', query: home };
  // City picker: only the city changed (the picker drops page/filters), so refresh home for that city.
  if (city && city !== currentQuery.city && withoutPage(home, ['city']) === withoutPage(currentQuery, ['city']))
    return { pathname: '/apphome', query: home };
  // city/category live in the /s path; as query keys they would duplicate it.
  const { city: _city, category: _category, ...searchQuery } = destinationQuery;
  return { pathname: destination.pathname, query: { ...searchQuery, ...shellQuery } };
};
